import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ReactNode } from 'react'

interface RoleBasedRedirectProps {
  children: ReactNode
}

/**
 * Component that restricts hotel owners and affiliates to specific routes.
 * Allows them to access their configured default dashboard.
 * Admin users are NOT restricted - they have full access to all routes.
 */
export const RoleBasedRedirect: React.FC<RoleBasedRedirectProps> = ({ children }) => {
  const { user } = useAuth()
  const location = useLocation()

  // Admin users (includes legacy global_admin) - have full access, skip restrictions
  const isAdmin = user?.userType === 'admin' || user?.userType === 'global_admin' ||
    user?.roles?.includes('admin') || user?.roles?.includes('global_admin')

  // Admin users have full access to all routes - no restrictions
  if (isAdmin) {
    return <>{children}</>
  }

  // Hotel users (includes legacy hotel_admin, hotel_manager, hotel_staff)
  const isHotel = user?.userType === 'hotel' || user?.userType === 'hotel_admin' ||
    user?.userType === 'hotel_manager' || user?.userType === 'hotel_staff' ||
    user?.roles?.includes('hotel') || user?.roles?.includes('hotel_admin')
  // Affiliate users (includes legacy affiliate_owner, affiliate_manager, affiliate_member)
  const isAffiliate = user?.userType === 'affiliate' || user?.userType === 'affiliate_owner' ||
    user?.userType === 'affiliate_manager' || user?.userType === 'affiliate_member' ||
    user?.roles?.includes('affiliate') || user?.roles?.includes('affiliate_owner')

  // Access by ROLE *or* ENTITY association, mirroring navigationSections'
  // hasHotelAccess/hasAffiliateAccess. The sidebar renders a menu for any
  // side the user has an entity on, so the guard must allow that side too —
  // otherwise a dual-role user (hotel userType + affiliate associations, or
  // vice versa) sees affiliate nav items that bounce to /dashboard because
  // the old role-only hotel branch ran first. (Architecture-review P0.)
  const hasHotelAccess = isHotel || ((user as any)?.hotels ?? []).some((h: any) => h.isActive)
  const hasAffiliateAccess = isAffiliate || ((user as any)?.affiliates ?? []).some((a: any) => a.isActive)

  // Allowed routes for hotels and affiliates. Each entry is a
  // prefix matched via startsWith() in isRouteAllowed below — so
  // '/contracts/' covers /contracts/:id, '/ugc' covers /ugc/new etc.
  //
  // PARITY ENFORCED IN CI. scripts/audit/scripts/check-route-allowlist.js
  // walks every <Route path> in App.tsx and asserts coverage here;
  // a missing prefix fails the audit-inventory-check workflow on
  // push, so this list cannot silently drift behind the route table
  // again. New shared routes need a prefix in BOTH lists; new
  // /hotel-* routes go in the hotel list only and /affiliate-*
  // routes in the affiliate list only.
  const allowedRoutesForHotels = [
    '/dashboard', '/hotel-dashboard', '/hotel-performance', '/hotel-visibility', '/hotel-reservations',
    '/hotel-affiliations', '/hotel-active-affiliations', '/hotel-pending-affiliations',
    '/hotel-details', '/hotels',
    // #2071 — the hotel commissions prefix is deliberately NOT listed: hotel
    // users no longer have that page (App.tsx wraps the route in the admin
    // ProtectedRoute and the hotel nav tree has no entry). Keep this comment
    // free of quoted paths: check-route-allowlist.js reads every quoted string
    // inside this array literal, comments included.
    '/discovery', '/messaging', '/promotions', '/hotel-promotions',
    '/hotel/verification',
    // #736 — '/ugc' removed: hotel users no longer have Content/UGC access, so
    // direct navigation to /ugc/* falls through to the /dashboard redirect.
    // #968 — '/contracts' removed likewise: hotel users no longer have contract
    // access; the page stays affiliate- and admin-only.
    // Shipped hotel features that were unreachable because the guard
    // never listed them (Architecture-review P0): tender marketplace,
    // per-hotel media bundles, chain details.
    '/marketplace', '/hotel-media-bundles', '/chain-details',
    // #284 — the /hotel-work-with-us prefix is deliberately NOT listed: the
    // embedded Work With Us builder is admin-only until a subscription gate
    // exists (App.tsx wraps the route in the admin ProtectedRoute and the hotel
    // nav tree has no entry). Re-add the prefix here when hotels get access.
    // Keep this comment free of quoted paths: check-route-allowlist.js reads
    // every quoted string inside this array literal, comments included.
    // '/commission-rules' retired — the route now redirects to /commissions/rules.
    // #2071 — the commissions prefix is deliberately NOT listed (in either
    // list): its only partner page is the affiliate Commission History, now
    // hidden. Listing it here would let a hotel + affiliate user reach that
    // page through this list. App.tsx wraps those routes in the admin
    // ProtectedRoute. The notifications prefix is not listed either: the
    // Notifications pages are hidden from hotel and chain hotel users too
    // (App.tsx makes that route admin-only as well).
    '/materials', '/payouts',
    '/profile', '/settings'
  ]
  const allowedRoutesForAffiliates = [
    '/dashboard', '/affiliate-dashboard', '/affiliate-performance', '/affiliate-visibility', '/affiliate-reservations',
    '/affiliate-hotels', '/affiliate-details',
    '/discovery', '/messaging', '/promotions',
    '/affiliate/verification',
    // #2071 — the trailing slash is deliberate: it admits a single contract
    // (the signing links land there) but not the contracts list itself, which
    // is hidden from affiliates. isRouteAllowed strips a trailing slash from
    // the path first, so the list with a trailing slash is refused too.
    '/contracts/', '/ugc',
    // Affiliates browse/apply to tenders in the marketplace.
    '/marketplace',
    // '/commission-rules' retired — the route now redirects to /commissions/rules.
    // #2071 — the commissions prefix is deliberately NOT listed: affiliates no
    // longer see Commission History, and the notifications prefix is not
    // listed either (see the hotel list above).
    '/materials', '/payouts',
    '/profile', '/settings'
  ]

  // Union of the lists the user has access to (role OR entity), so a
  // dual-role user gets both sides. Users with neither hotel nor affiliate
  // access fall through to render children unchanged.
  if (!hasHotelAccess && !hasAffiliateAccess) {
    return <>{children}</>
  }

  if (location.pathname === '/') {
    return <Navigate to="/dashboard" replace />
  }

  const allowed = [
    ...(hasHotelAccess ? allowedRoutesForHotels : []),
    ...(hasAffiliateAccess ? allowedRoutesForAffiliates : []),
  ]
  // Match on the path without a trailing slash, so an entry that ends in a
  // slash (a single-contract prefix) can never admit its own parent page.
  const path = location.pathname.replace(/\/+$/, '') || '/'
  const isRouteAllowed = allowed.some(route => path.startsWith(route))
  if (!isRouteAllowed) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
